/**
 * APTA numbers are issued as `XX-XXXXX` — two digits, a hyphen, five more.
 *
 * Players get theirs by copying it off the APTA member lookup page, and the
 * hyphen does not always survive the trip: on 2026-09-10 the server rejected
 * `1408192`, `1406893`, `1399118`, `1401381` and `1701196` inside one hour,
 * every one of them a real number that had lost its separator. Each of those
 * cost a round trip to APTA before coming back "Invalid APTA ID", so the shape
 * is worth settling in the browser.
 */

/** The canonical form, for display and for what we send to the server. */
const APTA_DIGITS = 7;

/**
 * The number in canonical `XX-XXXXX` form, or null if it cannot be one.
 *
 * Anything that is not a digit is discarded first, which is what makes the
 * common failures recoverable rather than merely rejected: a missing hyphen, a
 * space, an en dash pasted from the web page, a stray "APTA" prefix. What is
 * left must be exactly seven digits — we deliberately do not pad or truncate,
 * because a wrong number resolves to somebody else's record.
 */
export function normalizeAptaNumber(raw: string): string | null {
    const digits = raw.replace(/\D/g, "");
    if (digits.length !== APTA_DIGITS) return null;
    return `${digits.slice(0, 2)}-${digits.slice(2)}`;
}

/** Whether `raw` can be read as an APTA number at all. */
export function isValidAptaNumber(raw: string): boolean {
    return normalizeAptaNumber(raw) !== null;
}

/**
 * Guidance for a number that is not yet valid, or null once it is.
 *
 * Returns null for an untouched field too: a form should not open already
 * complaining about input the user has not had a chance to give.
 */
export function aptaNumberHint(raw: string): string | null {
    if (raw.trim() === "") return null;
    if (isValidAptaNumber(raw)) return null;
    const digits = raw.replace(/\D/g, "").length;
    if (digits < APTA_DIGITS) {
        return "An APTA number has 7 digits, like 21-02826.";
    }
    return "That looks like too many digits — an APTA number is 7, like 21-02826.";
}
